import React, { memo, useMemo } from 'react'
import { SWRConfig } from 'swr'
import isEqualFast from 'react-fast-compare'
import isUndefined from 'lodash/isUndefined'
import { SWRRootContextType } from './types'
import RootContext from './RootContext'

const ERROR_RETRY_COUNT = 5
const ERROR_RETRY_INTERVAL = 500

/**
 * @type SWRRootPropsType
 * this interface defines the set of properties supported by the SWRRoot React component
 */
type SWRRootPropsType = {
  children: React.ReactNode
} & SWRRootContextType

/**
 * @component SWRRoot
 * The <SWRRoot> React component, which holds global configuration for all context fetches
 */
export function SWRRoot({
  children,
  fetcher = window.fetch,
  rethrowContextFetchErrors,
  useCredentials,
  // any remaining properties will be sent to SWR global config
  ...swrConfigOverrides
}: SWRRootPropsType): JSX.Element {
  const swrConfig = useMemo(
    () => ({
      // built-in compare in SWR 2.0.0 seems to have some bugs
      compare: isEqualFast,
      errorRetryCount: ERROR_RETRY_COUNT,
      errorRetryInterval: ERROR_RETRY_INTERVAL,
      revalidateOnFocus: false,
      suspense: false, // off by default
      ...swrConfigOverrides
    }),
    [swrConfigOverrides]
  )
  const swrRootContext = useMemo(
    () => ({
      fetcher,
      // by default, rethrow context fetch errors if not using fetcher middleware
      // the baseline middleware behaves in a way such that we don't want to rethrow fetcher errors when using it
      rethrowContextFetchErrors: isUndefined(rethrowContextFetchErrors)
        ? fetcher === window.fetch
        : rethrowContextFetchErrors,
      useCredentials
    }),
    [fetcher, rethrowContextFetchErrors, useCredentials]
  )

  return (
    <SWRConfig value={swrConfig}>
      <RootContext.Provider value={swrRootContext}>
        {children}
      </RootContext.Provider>
    </SWRConfig>
  )
}

export default memo(SWRRoot)
